import { Badge } from "@design";
import { Wrench } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { GeneratingIndicator } from "@/components/chat/GeneratingIndicator";
import { MessageAuthor } from "@/components/chat/MessageAuthor";
import { Text } from "@/components/Text";
import { CitedMarkdown } from "./CitedMarkdown";
import type { ToolCallView } from "./chatStreamReducer";
import { splitCitations } from "./citations";
import type { ChatSource, NumberedSources } from "./messageSources";
import { CitedSources, SourceTags } from "./Sources";

export interface AssistantMessageProps {
  /** Agent name when an agent answered; defaults to AnthmAI. */
  name?: string | null;
  /** Formatted time, or a status while streaming. */
  meta?: string;
  text: string;
  sources: ChatSource[];
  /** The sources by number, which the text's `[n]` citations and the sources list use; defaults to `sources`. */
  citations?: NumberedSources;
  /** Live tool activity (streaming only). */
  tools?: ToolCallView[];
  streaming?: boolean;
  /** Translated failure text. */
  error?: string | null;
  stopped?: boolean;
  /** Live status shown in place of the generating indicator (e.g. a capacity wait). */
  notice?: ReactNode;
  /** What the reply is doing or did, above its text (a research run's progress, then its summary). */
  activity?: ReactNode;
  footer?: ReactNode;
}

/**
 * The list under the answer, numbered like the text's `[n]` when it cites any (or splits Cited / Also consulted).
 * An answer without citations lists its sources as before citations existed, with a repeated file merged (MHS-794).
 */
function listedSources(text: string, sources: ChatSource[], citations: NumberedSources): NumberedSources {
  if (citations.some((source) => source?.cited === true)) return citations;
  return splitCitations(text, (n) => citations[n - 1] != null) ? citations : sources;
}

/** One assistant turn: author row, sources, rendered markdown, citations; live while streaming. */
export function AssistantMessage({
  name,
  meta,
  text,
  sources,
  citations = sources,
  tools = [],
  streaming = false,
  error,
  stopped = false,
  notice,
  activity,
  footer,
}: Readonly<AssistantMessageProps>) {
  const { t } = useTranslation("chat");
  return (
    <article
      aria-label={t("message.assistantTurn", { name: name ?? t("composer.defaultAssistant") })}
      aria-busy={streaming}
    >
      <MessageAuthor kind="assistant" name={name ?? undefined} meta={meta} />
      <div className="mt-3 ml-10 space-y-3">
        {activity}
        <SourceTags sources={sources} />
        {streaming && tools.some((tool) => tool.status === "running") && (
          <div className="flex flex-wrap gap-1.5">
            {tools
              .filter((tool) => tool.status === "running")
              .map((tool) => (
                <Badge key={tool.id} variant="neutral" icon={<Wrench size={12} />}>
                  {t("message.usingTool", { name: tool.name })}
                </Badge>
              ))}
          </div>
        )}
        {text && <CitedMarkdown text={text} sources={citations} streaming={streaming} />}
        {notice}
        {!text && !notice && !activity && streaming && <GeneratingIndicator />}
        {error && (
          <Text tone="error" role="alert">
            {error}
          </Text>
        )}
        {stopped && (
          <Text variant="caption" tone="tertiary">
            {t("message.stopped")}
          </Text>
        )}
        {!streaming && <CitedSources sources={listedSources(text, sources, citations)} />}
        {footer}
      </div>
    </article>
  );
}
